// Verification scorecards, one per lead. Seven dimensions per the build spec:
// employment, company, property_history, social_presence, budget_alignment,
// location, lender. Each dimension has a 0-100 score, a confidence label,
// and a short evidence summary.
//
// status: pending | passed | flagged | failed

import { NOW_ISO, hoursAgo, daysAgo } from './leads.js';

const D = (score, confidence, evidence) => ({ score, confidence, evidence });

const dims = (employment, company, property_history, social_presence, budget_alignment, location, lender) => ({
  employment,
  company,
  property_history,
  social_presence,
  budget_alignment,
  location,
  lender,
});

export const verifications = [
  // ── HOT ────────────────────────────────────────────────────────────────
  {
    leadId: 'lead-sofia-martinez',
    overallScore: 91,
    status: 'passed',
    dimensions: dims(
      D(95, 'high', 'Confirmed Cardiologist at Baptist Health, 9-year tenure.'),
      D(92, 'high', 'Baptist Health verified via NPI registry and public profile.'),
      D(90, 'high', 'Owns current home in Pinecrest (7 years), clean title.'),
      D(88, 'high', 'Active professional profile, medical society memberships.'),
      D(94, 'high', 'Proof of funds letter on file ($6.2M liquid).'),
      D(93, 'high', 'Current Miami resident for 11 years.'),
      D(85, 'medium', 'Cash buyer — no lender required.'),
    ),
    flags: [],
    notes: 'Clean verification across the board, cash offer backed by Wells Fargo POF.',
    verifiedAt: hoursAgo(6),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-james-chen',
    overallScore: 88,
    status: 'passed',
    dimensions: dims(
      D(94, 'high', 'VP Engineering at Lumen Capital (NYSE: LMCP). Confirmed via company directory.'),
      D(90, 'high', 'Public company, strong financials.'),
      D(80, 'medium', 'Owns SF condo (2019). Currently listed for sale.'),
      D(92, 'high', 'Public profile: 4.8k connections, verified email.'),
      D(88, 'high', 'Comp $850k + RSUs. Pre-approved up to $8M by JPMorgan.'),
      D(82, 'medium', 'Relocating from SF to Miami, timeline confirmed.'),
      D(90, 'high', 'JPMorgan pre-approval letter (30-day validity) on file.'),
    ),
    flags: [],
    notes: 'Standard tech-exec relocation profile. Financing clean.',
    verifiedAt: hoursAgo(14),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-patricia-dubois',
    overallScore: 92,
    status: 'passed',
    dimensions: dims(
      D(90, 'high', 'Founder of verified Paris-based luxury goods house (20+ years).'),
      D(95, 'high', 'Company featured in Forbes and Les Échos. Registered in France.'),
      D(92, 'high', 'Owns properties in Paris (8e) and St. Tropez.'),
      D(88, 'high', 'Profiled in fashion press. Public profile and interviews.'),
      D(96, 'high', 'Private bank attestation (HSBC Private) confirms $40M+ liquid.'),
      D(82, 'medium', 'International buyer. FIRPTA considerations flagged for closing team.'),
      D(90, 'high', 'All-cash. FIRPTA withholding to be handled at closing.'),
    ),
    flags: [
      {
        dimension: 'location',
        severity: 'info',
        message: 'International buyer — FIRPTA withholding required at closing.',
      },
    ],
    notes: 'Ultra-high net worth international buyer. Fully verified.',
    verifiedAt: hoursAgo(24),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-carlos-mendez',
    overallScore: 85,
    status: 'passed',
    dimensions: dims(
      D(88, 'high', 'CFO at Grupo Mendez Industriales (CDMX).'),
      D(84, 'medium', 'Family-owned industrial group — private, but verifiable via Mexico registro público.'),
      D(80, 'medium', 'Owns residence in Polanco, CDMX.'),
      D(78, 'medium', 'Limited public social presence (common for LatAm UHNW).'),
      D(90, 'high', 'Citibanamex POF letter confirms $6M liquid USD.'),
      D(80, 'medium', 'Mexican national. FIRPTA applies.'),
      D(92, 'high', 'Cash transaction.'),
    ),
    flags: [
      {
        dimension: 'social_presence',
        severity: 'info',
        message: 'Limited online footprint consistent with LatAm UHNW norms.',
      },
    ],
    notes: 'Strong cash buyer. Pre-tour package sent in Spanish.',
    verifiedAt: hoursAgo(8),
    verifiedBy: 'ai',
  },

  // ── WARM ───────────────────────────────────────────────────────────────
  {
    leadId: 'lead-maria-lopez',
    overallScore: 72,
    status: 'passed',
    dimensions: dims(
      D(85, 'high', 'Partner at regional law firm, 12-year tenure.'),
      D(82, 'medium', 'Firm verified, solid reputation in Miami-Dade.'),
      D(70, 'medium', 'Owns Doral home (listed for sale in 30 days).'),
      D(72, 'medium', 'Professional profile, bar association listing.'),
      D(68, 'medium', 'Budget contingent on current home sale.'),
      D(88, 'high', 'Local buyer, 20+ years in Miami.'),
      D(55, 'medium', 'Needs lender intro — no pre-approval yet.'),
    ),
    flags: [
      {
        dimension: 'lender',
        severity: 'warning',
        message: 'No active pre-approval. Lender intro needed to progress.',
      },
    ],
    notes: 'Contingent sale adds friction but profile is solid.',
    verifiedAt: daysAgo(1),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-david-rodriguez',
    overallScore: 78,
    status: 'passed',
    dimensions: dims(
      D(86, 'high', 'Portfolio Manager at Coral Gables-based fund.'),
      D(80, 'medium', 'Private fund, discreet but verifiable via SEC IARD.'),
      D(82, 'medium', 'Owns current Gables home (5 years).'),
      D(75, 'medium', 'Moderate public profile presence.'),
      D(80, 'medium', 'Jumbo mortgage pre-qualification (not pre-approval) on file.'),
      D(92, 'high', 'Local, deeply rooted in community.'),
      D(70, 'medium', 'Needs final underwriting, intro sent to First Republic.'),
    ),
    flags: [],
    notes: 'Pre-qualified; upgrade to full pre-approval in progress.',
    verifiedAt: daysAgo(2),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-emily-watson',
    overallScore: 68,
    status: 'passed',
    dimensions: dims(
      D(82, 'high', 'Media executive at major NYC network.'),
      D(85, 'high', 'Fortune 500 employer, public.'),
      D(60, 'medium', 'NYC co-op owner (2015).'),
      D(78, 'medium', 'Public industry profile.'),
      D(65, 'medium', 'Dual-income, needs partner buy-in.'),
      D(58, 'medium', 'Remote-work relocation, not yet committed to move.'),
      D(55, 'medium', 'Loose pre-qualification, no firm letter.'),
    ),
    flags: [
      {
        dimension: 'location',
        severity: 'info',
        message: 'Still evaluating Miami vs staying in NYC.',
      },
    ],
    notes: 'Promising relo, needs discovery on partner and school fit.',
    verifiedAt: daysAgo(3),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-roberto-silva',
    overallScore: 74,
    status: 'passed',
    dimensions: dims(
      D(80, 'medium', 'Founder of São Paulo agribusiness (private).'),
      D(78, 'medium', 'Company active, verifiable via Receita Federal.'),
      D(72, 'medium', 'Owns São Paulo residence.'),
      D(68, 'medium', 'Low public profile, standard for LatAm founders.'),
      D(82, 'high', 'Bank attestation confirms $4M+ USD liquid.'),
      D(70, 'medium', 'Brazilian national. FIRPTA applies.'),
      D(82, 'high', 'All-cash transaction.'),
    ),
    flags: [],
    notes: 'Second-home / investment buyer. Wants rental-permissive building.',
    verifiedAt: daysAgo(4),
    verifiedBy: 'ai',
  },

  // ── NURTURE ────────────────────────────────────────────────────────────
  {
    leadId: 'lead-sarah-williams',
    overallScore: 52,
    status: 'pending',
    dimensions: dims(
      D(68, 'medium', 'Marketing director at regional agency.'),
      D(60, 'medium', 'Agency verified.'),
      D(40, 'low', 'Rents currently; no owned property record.'),
      D(62, 'medium', 'Moderate public profile.'),
      D(55, 'medium', 'Budget tight for stated neighborhoods.'),
      D(70, 'medium', 'Local resident.'),
      D(20, 'low', 'No lender engaged yet.'),
    ),
    flags: [
      {
        dimension: 'lender',
        severity: 'warning',
        message: 'No financing conversation started.',
      },
    ],
    notes: 'Long-horizon nurture, quarterly check-ins.',
    verifiedAt: daysAgo(11),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-michael-park',
    overallScore: 48,
    status: 'pending',
    dimensions: dims(
      D(72, 'medium', 'Senior engineer at SaaS scale-up.'),
      D(70, 'medium', 'Late-stage private, verifiable via Crunchbase.'),
      D(35, 'low', 'Renter in NYC.'),
      D(65, 'medium', 'Active developer and public profiles.'),
      D(40, 'low', 'Budget stretched for Brickell inventory.'),
      D(42, 'low', 'Still exploring relocation.'),
      D(18, 'low', 'No pre-approval.'),
    ),
    flags: [
      {
        dimension: 'budget_alignment',
        severity: 'warning',
        message: 'Budget below typical Brickell 1BR pricing in target buildings.',
      },
    ],
    notes: 'Needs inventory-reality conversation before more tours.',
    verifiedAt: daysAgo(14),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-ana-garcia',
    overallScore: 44,
    status: 'pending',
    dimensions: dims(
      D(58, 'medium', 'Small business owner, self-reported.'),
      D(45, 'low', 'Business not easily verifiable online.'),
      D(35, 'low', 'Renter.'),
      D(50, 'medium', 'Personal social only.'),
      D(40, 'low', 'Budget likely below target neighborhoods.'),
      D(65, 'medium', 'Miami-Dade local.'),
      D(15, 'low', 'No lender engaged.'),
    ),
    flags: [
      {
        dimension: 'company',
        severity: 'info',
        message: 'Business hard to verify — ask for entity name and website.',
      },
    ],
    notes: 'Low-intent nurture, reactivate after 3 months.',
    verifiedAt: daysAgo(22),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-thomas-brown',
    overallScore: 55,
    status: 'pending',
    dimensions: dims(
      D(80, 'high', 'PE partner at well-known Boston firm.'),
      D(82, 'high', 'Firm verified.'),
      D(70, 'medium', 'Owns Boston primary residence.'),
      D(65, 'medium', 'Limited social, consistent with PE partners.'),
      D(58, 'medium', 'Budget depends on bonus cycle.'),
      D(35, 'low', 'Not committed to Miami as second home yet.'),
      D(60, 'medium', 'All-cash when ready.'),
    ),
    flags: [
      {
        dimension: 'location',
        severity: 'info',
        message: 'Timeline 9-12 months; keep warm with curated listings.',
      },
    ],
    notes: 'High-quality future buyer, long timeline.',
    verifiedAt: daysAgo(9),
    verifiedBy: 'ai',
  },

  // ── COLD / DEAD ────────────────────────────────────────────────────────
  {
    leadId: 'lead-richard-smith',
    overallScore: 32,
    status: 'pending',
    dimensions: dims(
      D(25, 'low', 'No employment data available.'),
      D(20, 'low', 'No company info.'),
      D(30, 'low', 'No property record.'),
      D(35, 'low', 'Thin social presence.'),
      D(30, 'low', 'Budget well below team minimum.'),
      D(55, 'medium', 'Miami area.'),
      D(15, 'low', 'No lender.'),
    ),
    flags: [
      {
        dimension: 'employment',
        severity: 'warning',
        message: 'No verifiable employment or income data.',
      },
    ],
    notes: 'Unresponsive; sunset soon.',
    verifiedAt: daysAgo(30),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-jennifer-davis',
    overallScore: 28,
    status: 'failed',
    dimensions: dims(
      D(20, 'low', 'No employment data.'),
      D(15, 'low', 'No company info.'),
      D(25, 'low', 'No property records.'),
      D(30, 'low', 'Dormant profiles.'),
      D(20, 'low', 'Budget well below minimum.'),
      D(60, 'medium', 'Miami area.'),
      D(10, 'low', 'No lender, asked to stop contact.'),
    ),
    flags: [
      {
        dimension: 'social_presence',
        severity: 'error',
        message: 'Explicit opt-out received — do not contact.',
      },
    ],
    notes: 'Marked dead. Honor opt-out.',
    verifiedAt: daysAgo(70),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-kevin-hughes',
    overallScore: 30,
    status: 'pending',
    dimensions: dims(
      D(30, 'low', 'No employment data.'),
      D(25, 'low', 'No company info.'),
      D(30, 'low', 'No property record.'),
      D(30, 'low', 'Thin social footprint.'),
      D(40, 'low', 'Budget minimal for target market.'),
      D(25, 'low', 'Out of service area (Fort Lauderdale).'),
      D(15, 'low', 'No lender engaged.'),
    ),
    flags: [
      {
        dimension: 'location',
        severity: 'warning',
        message: 'Out of core service area.',
      },
    ],
    notes: 'Refer to Broward partner if re-engages.',
    verifiedAt: daysAgo(28),
    verifiedBy: 'ai',
  },

  // ── RED FLAG ───────────────────────────────────────────────────────────
  {
    leadId: 'lead-alexander-volkov',
    overallScore: 42,
    status: 'flagged',
    dimensions: dims(
      D(35, 'low', 'Claims CEO title; no verifiable employer online.'),
      D(20, 'low', 'No verifiable company registration in any jurisdiction.'),
      D(30, 'low', 'No property ownership records (US or international).'),
      D(15, 'low', 'No public profile, no press, no digital footprint.'),
      D(40, 'low', 'Claims $8.5M cash but no POF, no bank attestation.'),
      D(25, 'low', 'Phone area code is NYC (+1-917) but claims Moscow relocation.'),
      D(30, 'low', 'No lender, claims cash with no supporting documentation.'),
    ),
    flags: [
      {
        dimension: 'company',
        severity: 'error',
        message: 'Claims CEO of Moscow holding company with zero verifiable footprint.',
      },
      {
        dimension: 'social_presence',
        severity: 'error',
        message: 'No public profile, no professional mentions.',
      },
      {
        dimension: 'budget_alignment',
        severity: 'error',
        message: '$8.5M cash claim with no proof of funds or bank attestation.',
      },
      {
        dimension: 'location',
        severity: 'warning',
        message: 'NYC (+1-917) phone number inconsistent with stated Moscow location.',
      },
      {
        dimension: 'employment',
        severity: 'error',
        message: 'CEO claim cannot be reconciled with zero employer footprint.',
      },
    ],
    notes:
      'Multiple severe inconsistencies. Hold for manual review, run OFAC/sanctions screening before any contact.',
    verifiedAt: hoursAgo(12),
    verifiedBy: 'ai',
  },

  // ── DISQUALIFIED ───────────────────────────────────────────────────────
  {
    leadId: 'lead-frank-reed',
    overallScore: 18,
    status: 'failed',
    dimensions: dims(
      D(40, 'medium', 'Retail employee, hourly wage.'),
      D(30, 'low', 'National retailer, verified.'),
      D(20, 'low', 'No property record.'),
      D(25, 'low', 'Thin social.'),
      D(10, 'high', 'Budget $200-350k — well below team minimum.'),
      D(30, 'medium', 'Miami area.'),
      D(10, 'low', 'Would require 95% LTV mortgage.'),
    ),
    flags: [
      {
        dimension: 'budget_alignment',
        severity: 'error',
        message: 'Budget below brokerage minimum. Auto-disqualify.',
      },
    ],
    notes: 'Disqualified at intake. Referral sent to partner brokerage.',
    verifiedAt: daysAgo(24),
    verifiedBy: 'ai',
  },
  {
    leadId: 'lead-linda-brooks',
    overallScore: 22,
    status: 'failed',
    dimensions: dims(
      D(35, 'medium', 'Hospitality worker.'),
      D(30, 'low', 'Local hotel, verified.'),
      D(25, 'low', 'Rental history.'),
      D(25, 'low', 'Thin social.'),
      D(10, 'high', 'Budget $250-400k — well below team minimum.'),
      D(40, 'medium', 'Miami-Dade area.'),
      D(10, 'low', '97% LTV FHA-style financing needed.'),
    ),
    flags: [
      {
        dimension: 'budget_alignment',
        severity: 'error',
        message: 'Budget below brokerage minimum.',
      },
    ],
    notes: 'Disqualified at intake. Referral sent to partner brokerage.',
    verifiedAt: daysAgo(32),
    verifiedBy: 'ai',
  },
];

// Guard: verified stamp for "now" reference
export const VERIFICATIONS_SNAPSHOT_AT = NOW_ISO;

export const verificationByLeadId = Object.fromEntries(
  verifications.map((v) => [v.leadId, v]),
);
